import { Text, VSpacer } from '@/components/DesignSystem';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { QueryKeys } from '@/constants/QueryKeys';
import {
  EnergyProgramModal,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/Energy/EnergyProgramModal';
import {
  EnergyProgramReadiness,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/Energy/EnergyProgramReadiness';
import {
  EnergyTiersModal,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/Energy/EnergyTiersModal';
import {
  EnergyProgramForm,
  EnergyTierForm,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/Energy/energyValidation';
import {
  EnergyProgramRow,
} from '@/pages/Admin/HierarchyOfRetailers/Retailer/EnergyProgramRow';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { EnergyApi } from '@/utilities/api/EnergyApi';
import { ApiCategoryMapping } from '@api/interfaces/ApiCategoryMapping';
import { ApiEnergyProgramDetail } from '@api/interfaces/ApiEnergyProgramDetail';
import { ApiRewardsProgram } from '@api/interfaces/ApiRewardsProgram';
import { ProgramStatus } from '@shared/enums/ProgramStatus';
import { getProgramStatus } from '@shared/utilities';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';

interface EnergyProgramEntryProps {
  categoryMappings: ApiCategoryMapping[],
  energyPrograms: ApiRewardsProgram[],
  isNewProgram?: boolean,
  onRefresh: (detail?: ApiEnergyProgramDetail) => Promise<void>,
  program: ApiRewardsProgram,
}

type OpenModal = 'edit' | 'initialize' | 'markReady' | 'tiers' | null;

const toTierRequest = (tiers: EnergyTierForm[]) => tiers.map((tier) => ({
  behaviors: tier.behaviors.map((behavior) => ({
    description: behavior.description.trim(),
    id: behavior.id,
    isRequiredForTier: behavior.isRequiredForTier,
    name: behavior.name.trim(),
    sku: behavior.sku.trim() || null,
  })),
  behaviorsRequired: tier.behaviorsRequired,
  name: tier.name.trim(),
  pointsMultiplier: tier.pointsMultiplier,
}));

export const EnergyProgramEntry = ({
  categoryMappings,
  energyPrograms,
  isNewProgram = false,
  onRefresh,
  program,
}: EnergyProgramEntryProps) => {
  const { t } = useTranslation();
  const { openSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  // A program just created goes straight on to its tiers.
  const [openModal, setOpenModal] = useState<OpenModal>(isNewProgram ? 'tiers' : null);
  const [apiError, setApiError] = useState<string | null>(null);
  const isExpired = getProgramStatus(program) === ProgramStatus.Expired;

  const { data: programDetail } = useQuery(
    [QueryKeys.GET_ENERGY_PROGRAM, program.id],
    () => EnergyApi.getProgram(program.id),
  );

  const closeModal = () => {
    setApiError(null);
    setOpenModal(null);
  };

  const onModalError = (error: { message?: string }) => {
    setApiError(error.message || t(($) => $.energy.programModal.errors.generic));
  };

  const onDialogError = (error: { message?: string }) => {
    openSnackbar(error.message || t(($) => $.energy.programModal.errors.generic));
    closeModal();
  };

  const { isLoading: isUpdating, mutate: updateProgram } = useMutation(
    (form: Required<EnergyProgramForm>) => EnergyApi.updateProgram(program.id, {
      basePointsPerGallon: form.basePointsPerGallon,
      categoryMappingId: form.categoryMappingId,
      endDate: form.endDate!,
      name: form.name.trim(),
      pointsExpirationDate: form.pointsExpirationDate!,
      startDate: form.startDate!,
    }),
    {
      onError: onModalError,
      onSuccess: async (detail) => {
        openSnackbar(t(($) => $.energy.programModal.updated));
        await onRefresh(detail);
        closeModal();
      },
    },
  );

  const { isLoading: isSettingReady, mutate: setReady } = useMutation(
    (isReady: boolean) => EnergyApi.updateProgram(program.id, { isReady }),
    {
      onError: onDialogError,
      onSuccess: async (detail) => {
        openSnackbar(detail.isReady
          ? t(($) => $.energy.readiness.markedReady)
          : t(($) => $.energy.readiness.markedNotReady));
        await onRefresh(detail);
        closeModal();
      },
    },
  );

  const { isLoading: isSavingTiers, mutate: saveTiers } = useMutation(
    (tiers: EnergyTierForm[]) => EnergyApi.saveTiers(program.id, {
      tiers: toTierRequest(tiers),
    }),
    {
      onError: onModalError,
      onSuccess: async (detail) => {
        openSnackbar(t(($) => $.energy.tiers.saved));
        await onRefresh(detail);
        closeModal();
      },
    },
  );

  const { isLoading: isInitializing, mutate: initializeCustomers } = useMutation(
    () => EnergyApi.initializeCustomers(program.id),
    {
      onError: onDialogError,
      onSuccess: async ({ initialized }) => {
        openSnackbar(t(($) => $.energy.row.initialized, { count: initialized }));
        await queryClient.invalidateQueries(QueryKeys.GET_ENERGY_PROGRAM);
        closeModal();
      },
    },
  );

  return (
    <>
      {!!programDetail && !isExpired && (
        <>
          <EnergyProgramReadiness
            isSaving={isSettingReady}
            onMarkNotReady={() => setReady(false)}
            onMarkReady={() => setOpenModal('markReady')}
            program={programDetail}
          />
          <VSpacer size="4" />
        </>
      )}
      <EnergyProgramRow
        onEdit={() => setOpenModal('edit')}
        onInitializeCustomers={programDetail ? () => setOpenModal('initialize') : undefined}
        onManageBehaviors={programDetail ? () => setOpenModal('tiers') : undefined}
        program={program}
      />
      {openModal === 'edit' && !!programDetail && (
        <EnergyProgramModal
          apiError={apiError}
          categoryMappings={categoryMappings}
          existingProgram={programDetail}
          isSaving={isUpdating}
          onClose={closeModal}
          onSave={(form) => updateProgram(form)}
          otherEnergyPrograms={energyPrograms.filter(({ id }) => id !== program.id)}
        />
      )}
      {openModal === 'tiers' && !!programDetail && (
        <EnergyTiersModal
          apiError={apiError}
          isSaving={isSavingTiers}
          onClose={closeModal}
          onSave={(tiers) => saveTiers(tiers)}
          program={programDetail}
        />
      )}
      {!!programDetail && (
        <ConfirmDialog
          confirmText={t(($) => $.energy.readiness.markReady)}
          isLoading={isSettingReady}
          onCancel={closeModal}
          onConfirm={() => setReady(true)}
          open={openModal === 'markReady'}
          testID="energy-mark-ready-dialog"
          title={t(($) => $.energy.readiness.confirmTitle)}
        >
          <Text category="body-medium">
            {t(($) => $.energy.readiness.confirmBody, { startDate: programDetail.startDate })}
          </Text>
        </ConfirmDialog>
      )}
      <ConfirmDialog
        confirmText={t(($) => $.energy.row.initializeCustomers)}
        isLoading={isInitializing}
        onCancel={closeModal}
        onConfirm={() => initializeCustomers()}
        open={openModal === 'initialize'}
        testID="energy-initialize-customers-dialog"
        title={t(($) => $.energy.row.initializeConfirmTitle)}
      >
        <Text category="body-medium">
          {t(($) => $.energy.row.initializeConfirmBody)}
        </Text>
      </ConfirmDialog>
    </>
  );
};
